import { X } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import {
  MAX_TASK_NOTIFICATION_TOASTS,
  NOTIFICATION_CATEGORY_LABEL_BY_ID,
  NOTIFICATION_TOAST_VARIANT_BY_CATEGORY,
  TASK_NOTIFICATION_TOAST_DISMISS_MS,
} from '../../constants';
import styles from './TaskNotificationToasts.module.scss';

const ToastItem = ({ toast, onClose, onOpen }) => {
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLeaving(true);
    }, TASK_NOTIFICATION_TOAST_DISMISS_MS);

    return () => clearTimeout(timer);
  }, [toast.id]);

  const handleRemove = () => {
    setIsLeaving(true);
  };

  const handleAnimationEnd = () => {
    if (isLeaving) {
      onClose(toast.id);
    }
  };

  const categoryLabel = NOTIFICATION_CATEGORY_LABEL_BY_ID[toast.category] || 'Notification';
  const toastVariant = NOTIFICATION_TOAST_VARIANT_BY_CATEGORY[toast.category] || 'assignment';

  const toastVariantClass =
    styles[`toast${toastVariant.charAt(0).toUpperCase()}${toastVariant.slice(1)}`];

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${styles.toast} ${toastVariantClass} ${
        isLeaving ? styles.toastLeaving : styles.toastEntering
      }`}
      onClick={() => onOpen(toast)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(toast);
        }
      }}
      onAnimationEnd={handleAnimationEnd}
    >
      <span className={styles.title}>{toast.subject || categoryLabel}</span>
      <button
        type="button"
        className={styles.closeButton}
        aria-label="Close notification alert"
        onClick={(event) => {
          event.stopPropagation();
          handleRemove();
        }}
      >
        <X className={styles.closeIcon} />
      </button>
    </div>
  );
};

const TaskNotificationToasts = ({ toasts, onClose, onOpen }) => {
  if (!toasts.length) return null;

  return (
    <div className={styles.container}>
      {toasts.slice(0, MAX_TASK_NOTIFICATION_TOASTS).map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={onClose} onOpen={onOpen} />
      ))}
    </div>
  );
};

ToastItem.propTypes = {
  toast: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    category: PropTypes.number,
    subject: PropTypes.string,
    body: PropTypes.string,
    createdAt: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
};

TaskNotificationToasts.propTypes = {
  toasts: PropTypes.arrayOf(ToastItem.propTypes.toast),
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
};

export default TaskNotificationToasts;
